/**
 * Helper functions for wallet calculations with minimum payment requirement
 */

/**
 * Calculates wallet usage ensuring user pays at least ₹1
 * @param walletBalance - Current wallet balance
 * @param finalPayableAmount - Final amount after discounts
 * @returns Maximum wallet amount that can be used
 */
export const calculateWalletUsage = (
  walletBalance: number,
  finalPayableAmount: number
): number => {
  const maxWalletUsage = Math.max(0, finalPayableAmount - 1); // Ensure at least ₹1 payment
  return Math.min(walletBalance, maxWalletUsage);
};

/**
 * Calculates final amount after applying wallet deduction
 * @param finalPayableAmount - Final amount after discounts
 * @param walletUsage - Amount to deduct from wallet
 * @returns Final amount user needs to pay
 */
export const calculateFinalAmountAfterWallet = (
  finalPayableAmount: number,
  walletUsage: number
): number => {
  return Math.max(1, finalPayableAmount - walletUsage); // Ensure at least ₹1 payment
};

/**
 * Gets wallet usage message for UI display
 * @param walletBalance - Current wallet balance
 * @param finalPayableAmount - Final amount after discounts
 * @returns User-friendly message about wallet usage
 */
export const getWalletUsageMessage = (
  walletBalance: number,
  finalPayableAmount: number
): string => {
  const actualWalletUsage = calculateWalletUsage(
    walletBalance,
    finalPayableAmount
  );
  const maxWalletUsage = Math.max(0, finalPayableAmount - 1);

  if (actualWalletUsage === maxWalletUsage && walletBalance >= maxWalletUsage) {
    return `₹${actualWalletUsage} will be deducted from wallet (minimum ₹1 payment required)`;
  } else {
    return `₹${actualWalletUsage} will be deducted from wallet`;
  }
};
